export const defaultFont = "opensans" as const;

export const defaultAiAssistantInfo = {
	title: "Ai Learning Agent",
	alias: "AiLA",
	icon: "https://memorang-prod-media.s3.amazonaws.com/aila-default-icon.png",
	showOriginalQuestion: true,
	initialMessage:
		"👋 Hey there, I'm the **{{alias}}** learning assistant, and I'm here to help you better prepare for **{{context}}!** Let's specifically dig into the item you answered about **{{topic}}.**",
};

export const defaultAlertConfigs = {
	quiz: {
		severity: "info" as const,
		icon: "format-list-checks",
		description:
			"Start with a short, free diagnostic to familiarize yourself with the test format.",
	},
	practiceTest: {
		severity: "info" as const,
		icon: "clipboard-text-outline",
		description:
			"Assess your readiness with timed, full-length practice tests. Get detailed explanations, personalized feedback, and a score report after you finish.",
	},
	quizAndPracticeTest: {
		severity: "info" as const,
		icon: "format-list-checks",
		description:
			"Start with a free diagnostic to familiarize yourself with the test format or assess your readiness with timed, full-length practice tests. Get detailed explanations, personalized feedback, and a score report after you finish.",
	},
	topicMastery: {
		severity: "info" as const,
		icon: "chart-line",
		title: "Mastery Breakdown",
		description:
			"The table below shows your performance on each topic, the number of items you've answered so far, the proportion of the exam that the topic represents, and your opportunity for improvement. The greater the 'opportunity,' the better suited you will be by focusing your studies to strengthen your mastery of that topic to maximize your chance of passing.",
	},
};

export const defaultPracticeTestConfig = {
	header: {
		title: "Practice Tests",
		subtitle:
			"Take practice tests to assess your exam readiness, strengths, and opportunities for improvement",
	},
	label: "Practice Tests",
	summativeTestAlertConfig: defaultAlertConfigs,
	enabled: {
		mobile: false,
		web: true,
	},
	deviceRecommendationDialog: {
		enabled: false,
		title: "Best experience on laptop",
		message:
			"For the best experience, we recommend taking practice tests on a laptop or desktop computer.",
	},
};

export const defaultSessionViewConfig = {
	showFlightCalculator: false,
	showCrossOut: true,
	allowMarking: true,
};

export const defaultCitationsConfig = {
	visible: false,
	showTextBasedCitationOnly: false,
};

export const defaultDeploymentsConfig = {
	autoDeploymentEnabled: true,
};

export const defaultMaintenanceConfig = {
	title: "We'll be right back",
	subtitle:
		"We're currently making improvements to enhance your experience. We apologize for any inconvenience and appreciate your patience.",
};

export const defaultExamConfig = {
	practiceTest: defaultPracticeTestConfig,
	allowChangeExamDate: true,
};

export const defaultLeaderboardsConfig = {
	timePeriods: ["all_time", "month", "week"] as const,
	scopes: ["global"] as const,
	showUsersPodium: true,
	numUsersToDisplay: 10,
};

export const defaultI18nConfig = {
	supportedLocales: ["en-US"],
	fallbackLocale: "en-US",
	defaultToDeviceLocale: true,
	overrides: {},
};

/**
 * Default values for app config fields that have defaults in the Zod schema.
 * Used to merge with raw data when Zod validation is bypassed.
 */
export const defaultAppConfigValues = {
	app: {
		font: defaultFont,
		tagType: { "en-US": "Topic" },
		template: "testprep" as const,
		proficiencyTargetScore: 90,
		maxScaledScore: 100,
		aiAssistant: defaultAiAssistantInfo,
		isImageChallengeApp: false,
		isStudyPackEnabled: true,
		purchase: { isPaywallEnabled: true },
		showCountrySelection: false,
		deployments: defaultDeploymentsConfig,
		maintenance: defaultMaintenanceConfig,
		citations: defaultCitationsConfig,
		auth: { provider: "clerk" as const },
		accountSettings: { showPurchasedProducts: true },
		i18n: defaultI18nConfig,
	},
	exam: defaultExamConfig,
	sessionView: defaultSessionViewConfig,
	leaderboards: defaultLeaderboardsConfig,
};
